import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests from localStorage (streamToken is used by AuthApiService)
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('streamToken');
      localStorage.removeItem('streamUser');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class SettingsService {
  // Stripe Settings (admin only)
  async getStripeSettings() {
    try {
      const response = await axiosInstance.get('/settings/stripe');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Public Stripe config (any authenticated user)
  async getStripePublicConfig() {
    try {
      const response = await axiosInstance.get('/settings/stripe-public');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateStripeSettings(settings) {
    try {
      const response = await axiosInstance.put('/settings/stripe', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // SMTP Settings
  async getSmtpSettings() {
    try {
      const response = await axiosInstance.get('/settings/smtp');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateSmtpSettings(settings) {
    try {
      const response = await axiosInstance.put('/settings/smtp', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async testSmtp(testEmail) {
    try {
      const response = await axiosInstance.post('/settings/smtp/test', { test_email: testEmail });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Email Templates
  async getEmailTemplates() {
    try {
      const response = await axiosInstance.get('/settings/email-templates');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateEmailTemplate(templateName, template) {
    try {
      const response = await axiosInstance.put(`/settings/email-templates/${templateName}`, template);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // General Settings
  async getAllSettings() {
    try {
      const response = await axiosInstance.get('/settings');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getSettingsByCategory(category) {
    try {
      const response = await axiosInstance.get(`/settings/category/${category}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Twilio SMS Settings
  async getTwilioSettings() {
    try {
      const response = await axiosInstance.get('/settings/twilio');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateTwilioSettings(settings) {
    try {
      const response = await axiosInstance.put('/settings/twilio', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async testTwilioSms(testPhone) {
    try {
      const response = await axiosInstance.post('/settings/twilio/test', { test_phone: testPhone });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Social login (Google + Apple)
  async getSocialSettings() {
    try {
      const response = await axiosInstance.get('/settings/social');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateSocialSettings(settings) {
    try {
      const response = await axiosInstance.put('/settings/social', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // Registration verification channels (SMS OTP / Email OTP)
  async getVerificationSettings() {
    try {
      const response = await axiosInstance.get('/settings/verification');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateVerificationSettings(settings) {
    try {
      const response = await axiosInstance.put('/settings/verification', settings);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new SettingsService();
